import type { CommentedBlock } from './commented-blocks.js'

export interface CommentGroupOrder {
  block: CommentedBlock
  chatId: string
  /** ISO time of the thread's newest comment */
  latestActivity: string
}

/** Plan D3: the most recently active groups load first; older ones on scroll */
export const commentGroupLimit = 20

/**
 * Which anchored groups the sidebar Chat shows, and in which order (chat makeover M3, #2718; Joni's build notes).
 * A group is a commented block of the open song with at least one open comment in its thread. Groups sort by their
 * newest comment, oldest first, so the newest sits next to the composer, and only the newest `limit` show.
 */
export const orderCommentGroups = ({
  blocks,
  chats,
  comments,
  limit = commentGroupLimit,
}: {
  blocks: CommentedBlock[]
  chats: { id: string; parentId?: string }[]
  comments: { parentId?: string; createdAt?: string; resolved?: boolean }[]
  limit?: number
}) => {
  const chatByBlock = new Map(chats.map(chat => [chat.parentId, chat.id]))
  const threads = new Map<string, { latest: string; open: number }>()
  comments.forEach(({ parentId, createdAt = '', resolved }) => {
    if (!parentId) {
      return
    }
    const thread = threads.get(parentId) ?? { latest: '', open: 0 }
    threads.set(parentId, {
      latest: createdAt > thread.latest ? createdAt : thread.latest,
      open: thread.open + (resolved ? 0 : 1),
    })
  })

  const all = blocks
    .flatMap(block => {
      const chatId = chatByBlock.get(block.id)
      const thread = chatId ? threads.get(chatId) : undefined
      return chatId && thread && thread.open > 0 ? [{ block, chatId, latestActivity: thread.latest }] : []
    })
    .sort((a, b) => a.latestActivity.localeCompare(b.latestActivity))

  const older = Math.max(0, all.length - limit)
  return { groups: all.slice(older), older }
}
